/**
 * 动态表单弹窗组件
 *
 * JSON 配置驱动的表单弹窗，通过 fields 配置即可生成完整的表单弹窗。
 * 使用 antd Modal + Form + 标准输入组件，确保与 antd 主题一致。
 */

import { Col, Form, Modal, Row } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createLogger } from '../../utils/logger';
import { FormItemRenderer } from './field-renderer';
import type { DynamicFormModalProps } from './types';

const logger = createLogger({ prefix: 'DynamicFormModal' });

// 重新导出类型供外部使用
export type {
  DynamicFieldType,
  DynamicFormField,
  DynamicFormModalProps,
} from './types';

/**
 * 动态表单弹窗组件
 *
 * @example
 * ```tsx
 * <DynamicFormModal
 *   open={visible}
 *   title="新增用户"
 *   fields={[
 *     { name: 'name', label: '姓名', type: 'input', rules: [{ required: true }] },
 *     { name: 'email', label: '邮箱', type: 'input', rules: [{ pattern: /^\S+@\S+$/, message: '邮箱格式不正确' }] },
 *     { name: 'role', label: '角色', type: 'select', options: [
 *       { label: '管理员', value: 'admin' },
 *       { label: '用户', value: 'user' },
 *     ], rules: [{ required: true }] },
 *     { name: 'status', label: '启用', type: 'switch', defaultValue: true },
 *     { name: 'remark', label: '备注', type: 'textarea', rows: 3 },
 *   ]}
 *   onSubmit={async (values) => {
 *     const res = await createUser(values);
 *     return isApiSuccess(res);
 *   }}
 *   onCancel={() => setVisible(false)}
 * />
 * ```
 */
export function DynamicFormModal(props: DynamicFormModalProps) {
  const {
    open,
    title,
    fields,
    initialValues,
    onSubmit,
    onCancel,
    onError,
    okText,
    cancelText,
    width = 520,
    destroyOnClose = true,
    className,
    requiredMessage,
  } = props;

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false);

  // open: false -> true 时初始化表单
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      // 构建初始值
      const initVals: Record<string, unknown> = {};
      for (const field of fields) {
        if (initialValues?.[field.name] !== undefined) {
          initVals[field.name] = initialValues[field.name];
        } else if (field.defaultValue !== undefined) {
          initVals[field.name] = field.defaultValue;
        }
      }
      form.resetFields();
      form.setFieldsValue(initVals);
    }
    prevOpenRef.current = open;
  }, [open, fields, initialValues, form]);

  const handleOk = useCallback(async () => {
    if (loadingRef.current) return;

    try {
      loadingRef.current = true;
      setLoading(true);

      const values = await form.validateFields();
      const result = await onSubmit(values);
      if (result !== false) {
        onCancel();
      }
    } catch (err) {
      // 表单校验失败不算错误（antd Form.validateFields 抛出的 errorFields 对象）
      if (err && typeof err === 'object' && 'errorFields' in err) {
        return;
      }
      // onSubmit 抛出的异常由 onError 回调处理，避免 unhandled rejection
      // 注意：如果未提供 onError，用户无法感知提交失败，建议始终提供 onError 回调
      if (onError) {
        onError(err);
      } else {
        logger.error(
          'Submit error (provide onError prop for user-facing feedback):',
          err,
        );
      }
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [form, onSubmit, onCancel, onError]);

  return (
    <Modal
      title={title}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={loading}
      width={width}
      destroyOnClose={destroyOnClose}
      className={className}
    >
      <Form form={form} layout="vertical" autoComplete="off">
        <Row gutter={16}>
          {fields.map((field) => {
            if (field.hidden) return null;

            // 联动依赖：用 Form.Item 的 noStyle + shouldUpdate 实现
            if (field.dependencies?.length) {
              const depFields = field.dependencies.map((d) => d.field);
              return (
                <Form.Item
                  key={field.name}
                  noStyle
                  shouldUpdate={(prev, cur) =>
                    depFields.some((f) => prev[f] !== cur[f])
                  }
                >
                  {({ getFieldValue }) => {
                    const allMet = field.dependencies?.every((dep) =>
                      dep.condition(getFieldValue(dep.field)),
                    );
                    if (!allMet) return null;
                    return (
                      <Col span={field.span ?? 24}>
                        <FormItemRenderer
                          field={field}
                          requiredMessage={requiredMessage}
                        />
                      </Col>
                    );
                  }}
                </Form.Item>
              );
            }

            return (
              <Col key={field.name} span={field.span ?? 24}>
                <FormItemRenderer
                  field={field}
                  requiredMessage={requiredMessage}
                />
              </Col>
            );
          })}
        </Row>
      </Form>
    </Modal>
  );
}
